import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import { Pill } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { Card, ErrorBox, Facts, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import { day, num, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import { StockTable, type StockRow, type TransitRow } from "./Stock";

interface LocRow { code: string; name: string; operator: string | null; country: string | null; kind: string; is_bonded: boolean; entity: string | null; finished_cases: number; lines: number; as_of: string | null; sources: string[]; replaced: boolean }

export function Locations() {
  const { data, error, reload } = useApi<LocRow[]>("/locations");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  return (
    <>
      <PageHeader title="Locations" sub="Everywhere stock sits. Bonded stock (duty unpaid) is kept apart from duty-paid stock." />
      <Card>
        {!data ? <Loading /> : (
          <DataTable<LocRow> testId="locations-table" rows={data} rowKey={(r) => r.code} href={(r) => `/locations/${r.code}`} columns={[
            { key: "name", label: "Location", render: (r) => <><Link to={`/locations/${r.code}`}>{r.name}</Link>{r.is_bonded && <> <Pill tone="warning">bonded</Pill></>}</> },
            { key: "code", label: "Code", render: (r) => <code>{r.code}</code> },
            { key: "operator", label: "Operator" },
            { key: "kind", label: "Kind", render: (r) => titleCase(r.kind) },
            { key: "finished_cases", label: "Finished (cs)", numeric: true, render: (r) => num(r.finished_cases, 2) },
            { key: "as_of", label: "Latest data", render: (r) => (r.as_of ? day(r.as_of)
              : <span className="muted">{r.replaced ? "replaced by each warehouse's own reading" : "no data yet"}</span>) },
          ]} />
        )}
      </Card>
    </>
  );
}

interface LocDetail extends Omit<LocRow, "finished_cases" | "lines" | "as_of" | "sources"> {
  parts: { code: string; name: string; as_of: string | null }[];
  part_of: { code: string; name: string } | null;
  stock: StockRow[]; inbound_transit: TransitRow[]; outbound_transit: TransitRow[];
  snapshots: { as_of: string; run_id: number; source: string; rows: number; cases: number | null }[];
  parties: { id: number; name: string; kind: string }[];
}

export function LocationDetail() {
  const { code } = useParams();
  const { data, error, reload } = useApi<LocDetail>(`/locations/${code}`);
  const tabs = [
    { id: "stock", label: "Stock", count: data?.stock.length },
    { id: "transit", label: "In transit", count: data ? data.inbound_transit.length + data.outbound_transit.length : undefined },
    { id: "snapshots", label: "Snapshots", count: data?.snapshots.length },
  ];
  const [tab, setTab] = useTab(tabs);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const transit = [...data.inbound_transit.map((t) => ({ ...t, dir: "inbound" })), ...data.outbound_transit.map((t) => ({ ...t, dir: "outbound" }))];
  return (
    <>
      <PageHeader crumbs={[{ label: "Locations", to: "/locations" }]} title={data.name}
        badge={data.is_bonded ? <Pill tone="warning">bonded</Pill> : undefined} sub={<code>{data.code}</code>} />
      <Card>
        <Facts items={[["Operator", data.operator], ["Country", data.country], ["Kind", titleCase(data.kind)], ["Entity", data.entity],
          ["Address book", data.parties.length ? data.parties.map((p) => <Link key={p.id} to={`/settings/address-book/${p.id}`}>{p.name}</Link>) : null],
          ...(data.part_of ? [["Also counted in", <Link key="p" to={`/locations/${data.part_of.code}`}>{data.part_of.name}</Link>] as [string, ReactNode]] : [])]} />
      </Card>
      {data.parts.length > 0 && (
        <Card title={data.replaced ? "Not counted any more" : "Warehouses this location counts together"}>
          {data.replaced && (
            <p className="note" style={{ marginTop: 0 }} data-testid="replaced-note">
              This column counts every warehouse below as one. Each of them now has its own, newer reading,
              so this location's figures are no longer added into any stock total; counting both would count the
              same cases twice. Its earlier readings stay under Snapshots.
            </p>
          )}
          <DataTable rows={data.parts} rowKey={(p) => p.code} href={(p) => `/locations/${p.code}`} filterPlaceholder={false}
            columns={[
              { key: "name", label: "Warehouse", render: (p) => <Link to={`/locations/${p.code}`}>{p.name}</Link> },
              { key: "as_of", label: "Latest reading", render: (p) => (p.as_of ? day(p.as_of) : <span className="muted">none yet</span>) },
            ]} />
        </Card>
      )}
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      {tab === "stock" && <Card><StockTable rows={data.stock} hideLocation /></Card>}
      {tab === "transit" && (
        <Card>
          <DataTable rows={transit} rowKey={(t) => `${t.dir}-${t.document_id}-${t.sku}`} href={(t) => `/documents/${t.document_id}`}
            filterPlaceholder={false} empty="Nothing in transit to or from here." columns={[
              { key: "dir", label: "Direction", render: (t) => titleCase(t.dir) },
              { key: "number", label: "Transfer", render: (t) => `#${t.number}` },
              { key: "name", label: "Product" }, { key: "cases", label: "Cases", numeric: true, render: (t) => num(t.cases) },
              { key: "shipped", label: "Shipped", render: (t) => day(t.shipped) },
            ]} />
        </Card>
      )}
      {tab === "snapshots" && (
        <Card title="Every reading received for this location">
          <DataTable rows={data.snapshots} rowKey={(s) => `${s.run_id}-${s.as_of}`} href={(s) => `/imports/${s.run_id}`} filterPlaceholder={false}
            columns={[
              { key: "as_of", label: "As of", render: (s) => day(s.as_of) }, { key: "source", label: "Source" },
              { key: "rows", label: "Rows", numeric: true }, { key: "cases", label: "Cases", numeric: true, render: (s) => num(s.cases, 2) },
              { key: "run_id", label: "Import", render: (s) => <Link to={`/imports/${s.run_id}`}>#{s.run_id}</Link> },
            ]} />
        </Card>
      )}
    </>
  );
}
